import * as Sentry from '@sentry/react';
import { ApiError } from './types';
import { recordNetworkFailure, recordNetworkSuccess } from '../lib/networkFailureDetector';

export { ApiError } from './types';

interface ApiClientOptions extends RequestInit {
  token?: string;
  headers?: Record<string, string>;
}

function getErrorKey(status: number): string {
  switch (status) {
    case 401:
      return 'authExpired';
    case 413:
      return 'fileTooLarge';
    case 415:
      return 'unsupportedFormat';
    case 429:
      return 'rateLimit';
    case 502:
      return 'serviceUnavailable';
    case 504:
      return 'timeout';
    default:
      return 'uploadFailed';
  }
}

export async function apiClient<T = unknown>(path: string, options?: ApiClientOptions): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_URL;
  const url = `${baseUrl}${path}`;

  const { token, headers: customHeaders, ...fetchOptions } = options ?? {};

  const headers = new Headers(customHeaders);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const method = (fetchOptions.method ?? 'GET').toUpperCase();
  const timeoutMs = method === 'GET' ? 15_000 : 90_000;

  let response: Response;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: fetchOptions.signal ?? AbortSignal.timeout(timeoutMs),
    });
    recordNetworkSuccess();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Timeouts are NOT CORS-suspect — don't feed network failure detector
      const error = new ApiError('Request timed out', 0, 'timeout');
      Sentry.addBreadcrumb({
        category: 'network',
        message: 'Request timed out',
        level: 'warning',
        data: { path, method },
      });
      throw error;
    }
    if (err instanceof TypeError) {
      recordNetworkFailure();
      const error = new ApiError('Network error', 0, 'networkError');
      Sentry.addBreadcrumb({
        category: 'network',
        message: 'Network error — user may be offline',
        level: 'warning',
        data: { path, method },
      });
      throw error;
    }
    throw err;
  }

  if (!response.ok) {
    const errorKey = getErrorKey(response.status);
    const error = new ApiError(
      `Request failed with status ${response.status}`,
      response.status,
      errorKey,
    );
    if (response.status === 401) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Token expired or unauthorized',
        level: 'warning',
        data: { path, status: response.status },
      });
    } else {
      Sentry.captureException(error);
    }
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export async function sendQuoteEmail(
  token: string,
  quoteId: string,
  params: { to: string; message?: string; subject?: string },
): Promise<{ success: true }> {
  return apiClient<{ success: true }>(`/quotes/${quoteId}/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    token,
  });
}

export async function sendInvoiceEmail(
  token: string,
  invoiceId: string,
  params: { to: string; message?: string; subject?: string },
): Promise<{ success: true }> {
  return apiClient<{ success: true }>(`/invoices/${invoiceId}/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    token,
  });
}
